import {PepsiLogo} from '@/assets'
import {AccordionCustom as Accordion} from '@/components/molecules/AccordionCustom'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import './_promotionsReview.scss'
import PropTypes from 'prop-types'
import {setCurrentStep} from '@/store/slices/promotionStepSlice'
// Import the new components
import PromotionsReviewAdditionalInformation from './PromotionsReviewAdditionalInformation'
import PromotionsReviewAttributes from './PromotionsReviewAttributes'
import PromotionsReviewDisplay from './PromotionsReviewDisplay'
import PromotionsReviewSetup from './PromotionsReviewSetup'

// Default data updated to match real API structure
const defaultPromotionData = {
  name: 'APIPromotion_JLHtLZO5',
  promotionName: '',
  promotionType: 'BUY_SINGLE_PRODUCT',
  promotionConditions: [
    {
      sequence: 1,
      conditionGroup: {
        combinedWith: 'AND',
        conditions: [
          {
            type: 'BUY',
            quantity: 3,
            itemType: 'PRODUCT',
            items: ['Cheetos'],
            productCombination: true,
          },
        ],
      },
      rewards: [
        {
          type: 'FREE_PRODUCT',
          quantity: 1,
          items: ['Gamesa'],
        },
      ],
    },
  ],
  excludedItems: ['lays', 'doritos'],
  segments: {
    type: 'SELECTED_SEGMENTS',
    selectedSegments: ['4755919'],
    excludedSegments: ['Segment 4'],
  },
  bannerUrl: PepsiLogo,
  displayOn: ['HOME_PAGE', 'PRODUCT_PAGE'],
  displayOrder: 2,
  text: {
    en: {
      title: 'Buy One Get One Free',
      productTag: 'Product Tag',
      bannerTag: 'Buy 1 item, get another for free',
      excerpt: 'Excerpt text',
      description: 'English description',
    },
    'es-MX': {
      title: 'Buy One Get One Free Spanish',
      productTag: 'Product Tag spanish',
      bannerTag: 'Spanish banner',
      excerpt: 'Texto de extracto',
      description: 'Spanish description',
    },
  },
  activeStartDate: '2025-05-20',
  activeEndDate: '2025-10-29',
  active: true,
  maxAllowedOrderPerStore: 2,
  maxFreeItemsPerOrder: 10,
  resetFrequencyInDays: 7,
  behavior: 'Last Rule',
  id: '13e5bd64-a3fc-4bb8-8f39-ef31b98e91ed',
  externalPromotionId: '1234567',
  externalId: '1246788',
  conditionalExternalId: '1246897',
  benefitExternalId: '5678964',
  createdAt: '2025-05-20T17:56:42.854Z',
  status: 'PUBLISHED',
}

// Mock languages data as fallback
const mockLanguages = [
  {code: 'en', language: 'English'},
  {code: 'es-MX', language: 'Spanish (Mexico)'},
]

/**
 * PromotionsReview component displays detailed information about a promotion
 * @param {Object} props - Component props
 * @param {Object} [props.data] - Promotion data to display
 * @param {boolean} [props.isDetailView=false] - Whether this is being used in detail view (vs. creation flow)
 * @param {function} [props.setCurrentStep] - Function to set the current step
 */
const PromotionsReview = ({data, isDetailView = false, setCurrentStep = () => {}} = {}) => {
  const languagesFromHook = useFeatureFlagLanguages()
  const languages = languagesFromHook && languagesFromHook.length > 0 ? languagesFromHook : mockLanguages

  const promotionReviewData = data || defaultPromotionData

  const handleEditStep = step => {
    if (!isDetailView) {
      setCurrentStep(step)
    }
  }

  return (
    <div className="promotions-review w-full overflow-hidden">
      <Accordion
        isDark
        size="large"
        presentation="box-shadow"
        className="font-body w-full"
        content={
          <div className="flex w-full flex-col gap-4">
            {/* Setup Section */}
            <PromotionsReviewSetup
              promotionReviewData={promotionReviewData}
              isDetailView={isDetailView}
              handleEditStep={handleEditStep}
            />

            {/* Display Section */}
            <PromotionsReviewDisplay
              promotionReviewData={promotionReviewData}
              isDetailView={isDetailView}
              handleEditStep={handleEditStep}
              languages={languages}
            />

            {/* Attributes Section */}
            <PromotionsReviewAttributes
              promotionReviewData={promotionReviewData}
              isDetailView={isDetailView}
              handleEditStep={handleEditStep}
            />

            {/* Additional Information Section */}
            <PromotionsReviewAdditionalInformation
              promotionReviewData={promotionReviewData}
              isDetailView={isDetailView}
              handleEditStep={handleEditStep}
            />
          </div>
        }
      />
    </div>
  )
}

PromotionsReview.propTypes = {
  data: PropTypes.object,
  isDetailView: PropTypes.bool,
  setCurrentStep: PropTypes.func,
}

export default PromotionsReview
